import React, { memo, useImperativeHandle, RefObject, useCallback } from 'react';
import { DiagramEditor as DHTMLDiagramEditor, i18n } from 'dhx-diagram-package';
import { useDidMount } from '@core/utils/hooks/react';
import { IDataItem } from 'dhx-diagram-package/codebase/types/ts-data';
import { LibraryItemType } from '@business/entities/library';
import { LibraryBlockVariation } from '@modules/course/CourseBuilder/Blocks/LibraryBlock';

export const initialData = [
  {
    id: '1',
    text: 'Chairman & CEO',
    title: 'John Doe',
    type: 'img-card',
    width: 210,
    height: 90,
    headerColor: '#FF9800',
    fixed: false,
    editable: true,
    x: 0,
    y: 0,
    stroke: '#FF9800',
    dx: 590,
    dy: 10,
  },
];

const en = {
  applyAll: 'Apply all',
  exportData: 'Export data',
  importData: 'Import data',
  resetChanges: 'Reset changes',
  autoLayout: 'Auto layout',

  position: 'Position',
  size: 'Size',
  color: 'Color',
  title: 'Title',
  text: 'Name',
  image: 'Image',
  gridStep: 'Grid step',

  imageUpload: 'Image upload',
  emptyState: 'Empty state',
};

export interface DiagramEditorHandle {
  getData(): IDataItem[];
  setData(data: IDataItem[]): void;
  onExport(type: 'png' | 'pdf', name: string): void;
  isUndo(): boolean;
}
export interface DiagramEditorProps {
  id: string;
  handlesRef?: RefObject<DiagramEditorHandle>;
  data?: IDataItem[];
  type: LibraryItemType | LibraryBlockVariation;
}

export const DiagramEditor = memo((props: DiagramEditorProps) => {
  const { type } = props;
  const editor = React.useRef<DHTMLDiagramEditor | null>(null);

  const onExport = useCallback((type: 'png' | 'pdf', name: string) => {
    editor.current!.diagram.config.exportStyles = false;

    if (type === 'png') {
      editor.current?.diagram.export.png({});
    } else {
      editor.current?.diagram.export.pdf({ name: `${name}.pdf` });
    }
  }, []);

  useDidMount(() => {
    if (type === LibraryItemType.orgChart) {
      editor.current = new DHTMLDiagramEditor(props.id, {
        type: 'org',
        shapeType: 'img-card',
        reservedWidth: 0,
        editMode: true,
        controls: {
          gridStep: false,
          autoLayout: true,
          apply: false,
          reset: false,
          export: false,
          import: false,
        },
      });
      editor.current.parse(props.data || initialData);
      //   editor.current.diagram.config.margin = { x: 50 };
    } else {
      editor.current = new DHTMLDiagramEditor(props.id, {
        type: 'default',
        reservedWidth: 0,
        shapeToolbar: true,
        editMode: true,
        controls: {
          gridStep: false,
          autoLayout: true,
          apply: false,
          reset: false,
          export: false,
          import: false,
        },
      });
      if (props.data) {
        editor.current.parse(props.data);
      }
    }
  });

  const getData = useCallback(() => {
    return editor.current?.serialize() || [];
  }, []);

  const setData = useCallback((data: IDataItem[]) => {
    return editor.current?.parse(data);
  }, []);

  const isUndo = useCallback(() => {
    return editor.current?.history.isUndo() || false;
  }, []);

  useImperativeHandle(props.handlesRef, () => ({ getData, setData, onExport, isUndo }));

  return <div style={{ height: 'calc(100vh - 200px)' }} className="noselect dhx_sample-widget" id={props.id}></div>;
});
